import React, { useState, useMemo } from "react";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "./firebase";

// MUI imports
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, DarkMode, LightMode } from "@mui/icons-material";

// Framer Motion
import { motion, AnimatePresence } from "framer-motion";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Dynamic theme
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: "#b58c4d" },
          background: {
            default: darkMode ? "#0b0c0f" : "#f5f3ef",
            paper: darkMode ? "#14161b" : "#fffefa",
          },
        },
        shape: { borderRadius: 16 },
      }),
    [darkMode]
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      alert("✅ Login successful");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      alert("✅ Google login successful");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        component="main"
        maxWidth="xs"
        sx={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
      >
        {/* Dark/Light Toggle */}
        <Box sx={{ position: "absolute", top: 16, right: 16 }}>
          <motion.div whileTap={{ rotate: 360, scale: 0.9 }}>
            <IconButton onClick={() => setDarkMode(!darkMode)} color="primary">
              <AnimatePresence mode="wait" initial={false}>
                {darkMode ? (
                  <motion.div
                    key="light"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LightMode />
                  </motion.div>
                ) : (
                  <motion.div
                    key="dark"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <DarkMode />
                  </motion.div>
                )}
              </AnimatePresence>
            </IconButton>
          </motion.div>
        </Box>

        {/* Animated Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ width: "100%" }}
        >
          <Paper elevation={6} sx={{ p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Login
            </Typography>

            {error && (
              <Typography color="error" align="center" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>
                Login
              </Button>
            </Box>

            <Divider sx={{ my: 3 }}>OR</Divider>

            {/* Google Login */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleGoogleLogin}
              startIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 488 512"
                  fill="currentColor"
                >
                  <path d="M488 261.8C488 403.3 391.1 504 248 504 ..."></path>
                </svg>
              }
              sx={{
                backgroundColor: darkMode ? "#fff" : "#DB4437",
                color: darkMode ? "#000" : "#fff",
                "&:hover": {
                  backgroundColor: darkMode ? "#e0e0e0" : "#c1351d",
                },
              }}
            >
              Login with Google
            </Button>
          </Paper>
        </motion.div>
      </Container>
    </ThemeProvider>
  );
}
